"use client";

import React, { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { FileText, Loader2, Upload } from 'lucide-react';
import { readFileContent } from '@/utils/chatUtils';
import { toast } from '@/components/ui/use-toast';
import type { KnowledgeBaseDocument, LibrarianIngestResult } from '@/components/knowledge/types';
import type { DocumentBatchProgress, UploadedSourceDraft } from '@/components/knowledge/documentBatch';
import { formatDuration } from '@/components/knowledge/documentBatch';
import { cn } from '@/lib/utils';

const documentFormSchema = z.object({
  name: z.string().min(2, {
    message: 'Document name must be at least 2 characters.',
  }),
  content: z.string().min(2, {
    message: 'Document context must be at least 2 characters.',
  }),
});

interface DocumentDialogProps {
  mode: 'create' | 'edit';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: z.infer<typeof documentFormSchema>, fileType: string, size: number) => Promise<KnowledgeBaseDocument | null>;
  onProcessWithLibrarian?: (document: KnowledgeBaseDocument) => Promise<LibrarianIngestResult | null>;
  onStartBatchProcessing?: (sources: UploadedSourceDraft[], processWithLibrarian: boolean) => Promise<boolean>;
  batchProgress?: DocumentBatchProgress | null;
  document?: KnowledgeBaseDocument | null;
}

interface UploadedSource extends UploadedSourceDraft {
  file: File;
}

const FILE_ACCEPT = '.txt,.rtf,.pdf,.doc,.docx,.odt,.xls,.xlsx,.ods,.ppt,.pptx,.odp,.csv,.md,.html,.json,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.oasis.opendocument.spreadsheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.oasis.opendocument.text,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.oasis.opendocument.presentation,text/plain,text/rtf,application/rtf,text/markdown,text/html,application/json';

const DocumentDialog = ({
  mode,
  open,
  onOpenChange,
  onSubmit,
  onProcessWithLibrarian,
  onStartBatchProcessing,
  batchProgress,
  document,
}: DocumentDialogProps) => {
  const isCreateMode = mode === 'create';
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isStartingBatch, setIsStartingBatch] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [uploadedSources, setUploadedSources] = useState<UploadedSource[]>([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [isDragActive, setIsDragActive] = useState(false);
  const [currentFileType, setCurrentFileType] = useState<string>('text/plain');
  const [currentSize, setCurrentSize] = useState<number>(0);
  const [processWithLibrarian, setProcessWithLibrarian] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const documentForm = useForm<z.infer<typeof documentFormSchema>>({
    resolver: zodResolver(documentFormSchema),
    defaultValues: {
      name: document?.name || '',
      content: document?.content || '',
    },
  });

  const isBatchRunning = Boolean(batchProgress && batchProgress.status === 'running');
  const isBusy = isUploading || isSubmitting || isStartingBatch || isBatchRunning;
  const isCloseBlocked = isUploading || isSubmitting || isStartingBatch;
  const batchMode = isCreateMode && uploadedSources.length > 1;
  const previewSource = uploadedSources[previewIndex] ?? uploadedSources[0] ?? null;
  const isActivelyProcessing = isSubmitting || isStartingBatch || isBatchRunning;

  useEffect(() => {
    if (!open) return;

    if (document && !isCreateMode) {
      documentForm.reset({ name: document.name, content: document.content });
      setCurrentFileType(document.file_type);
      setCurrentSize(document.size);
      setUploadedSources([]);
      setPreviewIndex(0);
      setProcessWithLibrarian(true);
      setProcessingStatus('');
      return;
    }

    if (isCreateMode && !isBatchRunning) {
      documentForm.reset({ name: '', content: '' });
      setUploadedSources([]);
      setPreviewIndex(0);
      setCurrentFileType('text/plain');
      setCurrentSize(0);
      setProcessWithLibrarian(true);
      setProcessingStatus('');
      setIsDragActive(false);
    }
  }, [document, documentForm, isBatchRunning, isCreateMode, open]);

  useEffect(() => {
    if (!previewSource || isBatchRunning) return;
    documentForm.setValue('name', previewSource.name, { shouldValidate: true });
    documentForm.setValue('content', previewSource.content, { shouldValidate: true });
    setCurrentFileType(previewSource.fileType);
    setCurrentSize(previewSource.size);
  }, [documentForm, isBatchRunning, previewSource]);

  const processFiles = async (files: File[]) => {
    if (files.length === 0) return;

    setIsUploading(true);
    setProcessingStatus(files.length === 1 ? `Extracting ${files[0].name}…` : `Preparing ${files.length} source files…`);

    const nextSources: UploadedSource[] = [];

    try {
      for (const [index, file] of files.entries()) {
        setProcessingStatus(
          files.length === 1
            ? `Extracting ${file.name}…`
            : `Extracting file ${index + 1} of ${files.length}: ${file.name}`
        );

        try {
          const content = await readFileContent(file);
          nextSources.push({
            file,
            name: file.name,
            content,
            fileType: file.type || 'application/octet-stream',
            size: file.size,
            isPdf: file.type.includes('pdf') || file.name.endsWith('.pdf'),
          });
        } catch (error) {
          console.error('Error reading file:', error);
          toast({
            title: 'Error',
            description: `Failed to process ${file.name}.`,
            variant: 'destructive',
          });
        }
      }

      if (nextSources.length === 0) {
        setProcessingStatus('');
        return;
      }

      setUploadedSources(nextSources);
      setPreviewIndex(0);
      setProcessingStatus(
        nextSources.length === 1
          ? `${nextSources[0].name} is ready to save.`
          : `${nextSources.length} source documents are ready to save.`
      );
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    await processFiles(files);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);
    if (isBusy) return;
    const files = Array.from(event.dataTransfer.files ?? []);
    await processFiles(files);
  };

  const handleSubmit = async (values: z.infer<typeof documentFormSchema>) => {
    if (isCreateMode && uploadedSources.length > 1 && onStartBatchProcessing) {
      setIsStartingBatch(true);
      try {
        const started = await onStartBatchProcessing(
          uploadedSources.map(({ name, content, fileType, size, isPdf }) => ({ name, content, fileType, size, isPdf })),
          processWithLibrarian
        );

        if (!started) {
          toast({
            title: 'Batch already running',
            description: 'A Librarian batch is already in progress for this knowledge base.',
          });
        }
      } finally {
        setIsStartingBatch(false);
      }
      return;
    }

    setIsSubmitting(true);

    try {
      let fileType = currentFileType;
      let size = currentSize;

      if (isCreateMode) {
        if (previewSource) {
          fileType = previewSource.fileType;
          size = previewSource.size;
        } else {
          fileType = 'text/plain';
          size = values.content.length;
        }
      } else if (documentForm.formState.dirtyFields.content) {
        size = values.content.length;
      }

      setProcessingStatus(isCreateMode ? 'Saving source document…' : 'Updating source document…');
      const savedDocument = await onSubmit(values, fileType, size);
      if (!savedDocument) return;

      if (isCreateMode && processWithLibrarian && onProcessWithLibrarian) {
        setProcessingStatus('Processing with Librarian…');
        await onProcessWithLibrarian(savedDocument);
      }

      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
      setProcessingStatus('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      if (isCloseBlocked) return;
      onOpenChange(nextOpen);
    }}>
      <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden border-border/70 bg-background/95 p-0 shadow-2xl sm:max-w-[680px]">
        <DialogHeader className="border-b border-border/60 px-6 py-5">
          <DialogTitle>{isCreateMode ? 'Create Source Document' : 'Edit Source Document'}</DialogTitle>
          <DialogDescription>
            {isCreateMode
              ? 'Add raw source documents to this knowledge base. You can process them with Librarian right away.'
              : 'Update the source document metadata and content.'}
          </DialogDescription>
        </DialogHeader>
        <Form {...documentForm}>
          <form onSubmit={documentForm.handleSubmit(handleSubmit)} className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
              {isCreateMode && (
                <div className="space-y-4">
                  {!isActivelyProcessing && (
                    <div>
                      <FormLabel className="mb-2 block">Upload Source</FormLabel>
                      <div
                        className={cn(
                          'rounded-xl border-2 border-dashed bg-muted/10 p-5 text-center transition-colors',
                          isDragActive ? 'border-primary bg-primary/5' : 'border-border/70',
                          isBusy && 'opacity-80'
                        )}
                        onDragEnter={(event) => {
                          event.preventDefault();
                          if (!isBusy) setIsDragActive(true);
                        }}
                        onDragLeave={(event) => {
                          event.preventDefault();
                          if (event.currentTarget.contains(event.relatedTarget as Node)) return;
                          setIsDragActive(false);
                        }}
                        onDragOver={(event) => {
                          event.preventDefault();
                          if (!isBusy) setIsDragActive(true);
                        }}
                        onDrop={(event) => void handleDrop(event)}
                      >
                        <input
                          type="file"
                          className="hidden"
                          onChange={(event) => void handleFileInputChange(event)}
                          ref={fileInputRef}
                          multiple
                          accept={FILE_ACCEPT}
                        />
                        <button
                          type="button"
                          className="flex w-full flex-col items-center justify-center gap-2"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isBusy}
                        >
                          {isBusy ? (
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                          ) : previewSource?.isPdf ? (
                            <FileText className="h-8 w-8 text-muted-foreground" />
                          ) : (
                            <Upload className="h-8 w-8 text-muted-foreground" />
                          )}
                          <span className="text-sm font-medium">
                            {uploadedSources.length > 1
                              ? `${uploadedSources.length} files selected`
                              : previewSource
                                ? previewSource.name
                                : 'Click to upload or drag and drop files'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            Drop one or many raw source files here. Librarian can turn them into vault pages.
                          </span>
                        </button>
                      </div>
                    </div>
                  )}

                  {isActivelyProcessing && uploadedSources.length > 0 && (
                    <div className="rounded-xl border border-border/70 bg-card/60 px-4 py-3 text-sm text-muted-foreground">
                      Processing {uploadedSources.length === 1 ? previewSource?.name ?? '1 source' : `${uploadedSources.length} selected sources`}.
                    </div>
                  )}

                  {uploadedSources.length > 0 && !isActivelyProcessing && (
                    <div className="space-y-3 rounded-xl border border-border/70 bg-card/60 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-medium">Selected sources</div>
                          <div className="text-xs text-muted-foreground">
                            {uploadedSources.length === 1
                              ? 'Single source ready to save.'
                              : 'Batch mode: each file will be saved as its own source document unless it is already in this knowledge base.'}
                          </div>
                        </div>
                        {uploadedSources.length > 1 && (
                          <div className="text-xs text-muted-foreground">Previewing {previewIndex + 1} of {uploadedSources.length}</div>
                        )}
                      </div>

                      <div className="max-h-32 space-y-2 overflow-y-auto pr-1">
                        {uploadedSources.map((source, index) => (
                          <button
                            key={`${source.name}-${index}`}
                            type="button"
                            className={cn(
                              'flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left transition-colors',
                              index === previewIndex ? 'border-primary bg-primary/5' : 'border-border/60 hover:bg-muted/40'
                            )}
                            onClick={() => setPreviewIndex(index)}
                            disabled={isBusy}
                          >
                            <div className="min-w-0">
                              <div className="truncate text-sm font-medium">{source.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {(source.size / 1024).toFixed(1)} KB • {source.fileType || 'application/octet-stream'}
                              </div>
                            </div>
                            <FileText className="ml-3 h-4 w-4 shrink-0 text-muted-foreground" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {!isActivelyProcessing && (
                    <div className="flex items-start gap-3 rounded-xl border border-border/70 bg-muted/20 p-4">
                      <Checkbox
                        id="process-with-librarian"
                        checked={processWithLibrarian}
                        onCheckedChange={(checked) => setProcessWithLibrarian(Boolean(checked))}
                        disabled={isBusy}
                      />
                      <div className="space-y-1">
                        <label htmlFor="process-with-librarian" className="text-sm font-medium">
                          Process with Librarian
                        </label>
                        <p className="text-xs text-muted-foreground">
                          Create or refresh knowledge vault.
                        </p>
                        <p className="text-xs text-muted-foreground">
                          The process can take several minutes to hours to finish processing.
                        </p>
                      </div>
                    </div>
                  )}

                  {batchProgress && (
                    <div className="space-y-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-4 text-sm text-foreground">
                      <div className="flex items-start gap-3">
                        <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-primary" />
                        <div>
                          <div className="font-medium">{batchProgress.processingStatus}</div>
                          <div className="text-xs text-muted-foreground">
                            Saved {batchProgress.savedCount} of {batchProgress.totalSources} • Librarian processed {batchProgress.processedCount} • Skipped {batchProgress.skippedCount}
                          </div>
                        </div>
                      </div>

                      <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-3">
                        <div className="rounded-lg border border-border/60 bg-background/70 px-3 py-2">
                          <div className="font-medium text-foreground">Elapsed</div>
                          <div>{formatDuration(Date.now() - batchProgress.startedAt)}</div>
                        </div>
                        <div className="rounded-lg border border-border/60 bg-background/70 px-3 py-2">
                          <div className="font-medium text-foreground">Average per source</div>
                          <div>{batchProgress.completedCount > 0 ? formatDuration(batchProgress.averageItemMs) : 'After first source'}</div>
                        </div>
                        <div className="rounded-lg border border-border/60 bg-background/70 px-3 py-2">
                          <div className="font-medium text-foreground">Estimated remaining</div>
                          <div>{batchProgress.completedCount > 0 ? formatDuration(batchProgress.estimatedRemainingMs) : 'Calculating after first source'}</div>
                        </div>
                      </div>

                      {batchProgress.status === 'running' && (
                        <p className="text-xs text-muted-foreground">
                          You can close this dialog and keep using MapleOS while Librarian continues in the background.
                        </p>
                      )}
                    </div>
                  )}

                  {!batchProgress && processingStatus && (
                    <div className="flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-foreground">
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      <span>{processingStatus}</span>
                    </div>
                  )}
                </div>
              )}

              {batchMode && !isActivelyProcessing ? (
                <div className="rounded-xl border border-border/70 bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
                  Each selected file will keep its own extracted name and content. Click save when ready.
                </div>
              ) : (
                <>
                  <FormField
                    control={documentForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Annual research notes"
                            disabled={isBusy}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={documentForm.control}
                    name="content"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Content</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Paste document content or upload a file above"
                            className="min-h-[220px] resize-none"
                            disabled={isBusy}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}
            </div>
            <DialogFooter className="mt-auto flex items-center justify-between gap-3 border-t border-border/60 bg-background px-6 py-4">
              <div className="min-h-5 flex-1 text-sm text-muted-foreground">
                {isBusy && isCreateMode ? (batchProgress?.spinnerText ?? processingStatus) : ''}
              </div>
              <Button type="submit" disabled={isBusy}>
                {isBusy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isUploading
                      ? 'Preparing sources…'
                      : isBatchRunning || isStartingBatch
                        ? `Saving ${uploadedSources.length} sources…`
                        : isCreateMode
                          ? 'Saving source…'
                          : 'Updating source…'}
                  </>
                ) : isCreateMode ? 'Save source' : 'Update source'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentDialog;